import axios, { type AxiosInstance, type AxiosError } from 'axios';

// En dev : VITE_API_BASE_URL=http://localhost:3000 (requêtes directes, proxy Vite ignoré)
// En prod : VITE_API_BASE_URL=https://api.dashboard.domainename.fr
// Les cookies HttpOnly sont gérés par le backend, pas stockés en JS
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

let refreshPromise: Promise<void> | null = null;
let isCheckingAuth = false;

export function setCheckingAuth(value: boolean): void {
  isCheckingAuth = value;
}

let onAuthFailure: (() => void) | null = null;

export function setAuthFailureHandler(handler: () => void): void {
  onAuthFailure = handler;
}

// Flag non-sensible pour savoir si une session existe (pas le token lui-même)
export function setSessionActive(active: boolean): void {
  if (active) {
    localStorage.setItem('session_active', '1');
  } else {
    localStorage.removeItem('session_active');
  }
}

export function isSessionActive(): boolean {
  return localStorage.getItem('session_active') === '1';
}

// Compatibilité legacy (utilisé par quelques composants)
export function setTokens(_accessToken: string, _refreshToken: string): void {
  setSessionActive(true);
}

export function clearTokens(): void {
  setSessionActive(false);
}

async function doRefresh(client: AxiosInstance): Promise<void> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = client
    .post('/auth/refresh') // Le refresh token est dans le cookie HttpOnly
    .then(() => { /* Les nouveaux cookies sont posés par le backend */ })
    .catch((err) => {
      setSessionActive(false);
      throw err;
    })
    .finally(() => { refreshPromise = null; });

  return refreshPromise;
}

export const client: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Envoie les cookies HttpOnly avec chaque requête
  headers: { 'Content-Type': 'application/json' },
});

// Pas d'intercepteur de requête pour Authorization header : les cookies sont envoyés automatiquement

// Intercepteur de réponse : retry après refresh sur 401
client.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest.headers['X-Retry'] &&
      !originalRequest.url?.endsWith('/auth/refresh')
    ) {
      try {
        await doRefresh(client);
        originalRequest.headers['X-Retry'] = 'true';
        return client(originalRequest);
      } catch {
        setSessionActive(false);
        if (onAuthFailure && !isCheckingAuth) {
          onAuthFailure();
        }
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

export default client;
